import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Mail, Instagram, ArrowUpRight, ChevronRight } from "lucide-react";
import { BookACallButton } from "@/components/BookACallButton";
import {
    LOGIN_URL,
    BOOK_A_CALL_URL,
    SITE,
    CONTACT,
    ABOUT_DROPDOWN,
} from "@/constants/site";
import { SYSTEMS } from "@/data/systems";
import { SystemIcon } from "@/components/SystemIcon";
import { cn } from "@/lib/utils";
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

function NavTextLink({ to, children, end = false, testId }) {
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={testId}
            className={({ isActive }) =>
                cn(
                    "relative inline-flex items-center px-4 py-2 text-[15px] font-display font-bold tracking-tight transition-colors duration-200",
                    isActive
                        ? "text-[#0a0b0d]"
                        : "text-[#0a0b0d]/75 hover:text-[#0a0b0d]",
                )
            }
        >
            {({ isActive }) => (
                <>
                    <span>{children}</span>
                    <span
                        className={cn(
                            "absolute left-3 right-3 -bottom-0.5 h-[3px] rounded-full transition-all duration-200",
                            isActive
                                ? "bg-[#188bf6] opacity-100"
                                : "bg-[#188bf6] opacity-0 group-hover:opacity-40",
                        )}
                        aria-hidden="true"
                    />
                </>
            )}
        </NavLink>
    );
}

function NavDropdownTrigger({ label, open, testId }) {
    return (
        <button
            type="button"
            data-testid={testId}
            className={cn(
                "inline-flex items-center gap-1 px-4 py-2 text-[15px] font-display font-bold tracking-tight transition-colors duration-200",
                open
                    ? "text-[#188bf6]"
                    : "text-[#0a0b0d]/75 hover:text-[#0a0b0d]",
            )}
        >
            <span>{label}</span>
            <ChevronDown
                className={cn(
                    "w-4 h-4 transition-transform duration-200",
                    open ? "rotate-180 text-[#188bf6]" : "",
                )}
                aria-hidden="true"
            />
        </button>
    );
}

export function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [systemsOpen, setSystemsOpen] = useState(false);
    const [aboutOpen, setAboutOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        // close all menus on route change — intentional state sync after navigation.
        // Generic disable used (instead of a specific rule name) so it works under
        // both the dev server's older eslint config and stricter CI lints.
        // eslint-disable-next-line
        setMobileOpen(false);
        setSystemsOpen(false);
        setAboutOpen(false);
    }, [location.pathname, location.hash]);

    return (
        <header
            className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/85"
            data-testid="site-navbar"
        >
            {/* UTILITY BAR */}
            <div className="bg-[#0a0b0d] text-white text-[12px]" data-testid="utility-bar">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-9">
                        <div className="hidden sm:flex items-center gap-3 text-white/75 font-display font-medium">
                            <span>One plan. Seven systems.</span>
                            <span className="h-3 w-px bg-white/20" aria-hidden="true" />
                            <span>No bull*. No contracts.</span>
                            <span className="h-3 w-px bg-white/20" aria-hidden="true" />
                            <span>Serving home service operators.</span>
                        </div>
                        <div className="flex sm:hidden items-center gap-2 text-white/80 font-display font-medium">
                            <span>No bull* marketing systems.</span>
                        </div>
                        <div className="flex items-center gap-4 text-white/85 font-display font-medium">
                            <a
                                href={CONTACT.phoneHref}
                                data-testid="utility-phone-link"
                                className="inline-flex items-center gap-1.5 hover:text-[#188bf6] transition-colors"
                            >
                                <Phone className="w-3.5 h-3.5" aria-hidden="true" />
                                <span className="hidden sm:inline">{CONTACT.phone}</span>
                            </a>
                            <a
                                href={`mailto:${CONTACT.email}`}
                                data-testid="utility-email-link"
                                className="hidden md:inline-flex items-center gap-1.5 hover:text-[#188bf6] transition-colors"
                            >
                                <Mail className="w-3.5 h-3.5" aria-hidden="true" />
                                <span>{CONTACT.email}</span>
                            </a>
                            <a
                                href={CONTACT.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="utility-instagram-link"
                                aria-label="Instagram"
                                className="hidden sm:inline-flex items-center hover:text-[#188bf6] transition-colors"
                            >
                                <Instagram className="w-3.5 h-3.5" aria-hidden="true" />
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* MAIN NAV */}
            <div
                className={cn(
                    "transition-shadow duration-200 border-b",
                    scrolled
                        ? "border-[#0a0b0d]/8 shadow-[0_4px_18px_-12px_rgba(10,11,13,0.18)]"
                        : "border-[#0a0b0d]/8",
                )}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-[72px] sm:h-[84px]">
                        {/* LOGO */}
                        <Link
                            to="/"
                            data-testid="site-logo-link"
                            aria-label={`${SITE.name} home`}
                            className="flex items-center gap-3 group focus-ring"
                        >
                            <img
                                src="/assets/mascot_pose1.png"
                                alt=""
                                className="h-11 sm:h-14 w-auto select-none"
                                loading="eager"
                                draggable="false"
                                aria-hidden="true"
                            />
                            <span className="flex flex-col leading-none">
                                <span className="font-display font-extrabold text-base sm:text-lg tracking-tight text-[#0a0b0d]">
                                    ELEVARA
                                </span>
                                <span className="font-display font-bold text-[10px] sm:text-[11px] tracking-[0.22em] text-[#188bf6] mt-0.5">
                                    SYSTEMS
                                </span>
                            </span>
                        </Link>

                        {/* DESKTOP NAV */}
                        <nav className="hidden lg:flex items-center gap-1">
                            <NavTextLink to="/" end testId="nav-link-home">
                                Home
                            </NavTextLink>

                            {/* SYSTEMS DROPDOWN */}
                            <DropdownMenu open={systemsOpen} onOpenChange={setSystemsOpen}>
                                <DropdownMenuTrigger asChild>
                                    <div>
                                        <NavDropdownTrigger
                                            label="Systems"
                                            open={systemsOpen}
                                            testId="nav-link-systems"
                                        />
                                    </div>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="center"
                                    sideOffset={14}
                                    data-testid="systems-dropdown-menu"
                                    className="w-[640px] p-5 rounded-2xl border border-[#0a0b0d]/10 bg-white shadow-[0_24px_60px_-20px_rgba(10,11,13,0.25)]"
                                >
                                    <div className="px-1 pb-3 mb-3 border-b border-[#0a0b0d]/8 flex items-center justify-between">
                                        <span className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#4a5562]">
                                            The Stack
                                        </span>
                                        <span className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#188bf6]">
                                            7 systems / one plan
                                        </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-1">
                                        {SYSTEMS.map((sys) => (
                                            <Link
                                                key={sys.slug}
                                                to={`/systems/${sys.slug}`}
                                                data-testid={`systems-dropdown-item-${sys.slug}`}
                                                className="group flex items-start gap-3 p-3 rounded-xl hover:bg-[#188bf6]/8 transition-colors"
                                            >
                                                <span className="shrink-0 w-10 h-10 rounded-lg border border-[#0a0b0d]/10 bg-white group-hover:border-[#188bf6]/55 group-hover:bg-[#188bf6]/10 flex items-center justify-center transition-colors">
                                                    <SystemIcon
                                                        slug={sys.slug}
                                                        className="w-5 h-5 text-[#188bf6]"
                                                    />
                                                </span>
                                                <span className="min-w-0">
                                                    <span className="block font-display font-bold text-[14px] text-[#0a0b0d] tracking-tight">
                                                        {sys.name}
                                                    </span>
                                                    <span className="block mt-0.5 text-[12px] text-[#4a5562] leading-snug line-clamp-2">
                                                        {sys.tagline}
                                                    </span>
                                                </span>
                                            </Link>
                                        ))}
                                    </div>
                                    <div className="mt-4 pt-3 border-t border-[#0a0b0d]/8 flex items-center justify-between">
                                        <span className="text-[12px] text-[#4a5562]">
                                            Want the whole stack walked through?
                                        </span>
                                        <BookACallButton
                                            size="sm"
                                            shimmer={false}
                                            testId="systems-dropdown-book-a-call"
                                        />
                                    </div>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            <NavTextLink to="/pricing" testId="nav-link-pricing">
                                Pricing
                            </NavTextLink>

                            {/* ABOUT DROPDOWN */}
                            <DropdownMenu open={aboutOpen} onOpenChange={setAboutOpen}>
                                <DropdownMenuTrigger asChild>
                                    <div>
                                        <NavDropdownTrigger
                                            label="About"
                                            open={aboutOpen}
                                            testId="nav-link-about"
                                        />
                                    </div>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                    align="center"
                                    sideOffset={14}
                                    data-testid="about-dropdown-menu"
                                    className="w-[360px] p-3 rounded-2xl border border-[#0a0b0d]/10 bg-white shadow-[0_24px_60px_-20px_rgba(10,11,13,0.25)]"
                                >
                                    <div className="px-2 py-2 mb-1 flex items-center justify-between">
                                        <span className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#4a5562]">
                                            Who we are
                                        </span>
                                    </div>
                                    {ABOUT_DROPDOWN.map((item) => (
                                        <Link
                                            key={item.to}
                                            to={item.to}
                                            data-testid={`about-dropdown-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                            className="group flex items-center justify-between gap-3 p-3 rounded-xl hover:bg-[#188bf6]/8 transition-colors"
                                        >
                                            <span className="min-w-0">
                                                <span className="block font-display font-bold text-[14px] text-[#0a0b0d] tracking-tight">
                                                    {item.label}
                                                </span>
                                                <span className="block mt-0.5 text-[12px] text-[#4a5562] leading-snug">
                                                    {item.description}
                                                </span>
                                            </span>
                                            <ChevronRight
                                                className="w-4 h-4 text-[#0a0b0d]/30 group-hover:text-[#188bf6] transition-colors"
                                                aria-hidden="true"
                                            />
                                        </Link>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>

                            {/* CONTACT US — DIRECT EXTERNAL */}
                            <a
                                href={BOOK_A_CALL_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="nav-link-contact"
                                className="inline-flex items-center gap-1 px-4 py-2 text-[15px] font-display font-bold tracking-tight text-[#0a0b0d]/75 hover:text-[#188bf6] transition-colors"
                            >
                                <span>Contact Us</span>
                                <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
                            </a>
                        </nav>

                        {/* RIGHT CTAs */}
                        <div className="hidden lg:flex items-center gap-3">
                            <a
                                href={LOGIN_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="login-nav-link"
                                className="h-11 px-4 inline-flex items-center text-sm font-display font-bold text-[#0a0b0d] border-2 border-[#0a0b0d]/12 hover:border-[#0a0b0d] rounded-xl transition-colors duration-200"
                            >
                                Login
                            </a>
                            <BookACallButton
                                size="md"
                                pulse
                                testId="book-a-call-nav-button"
                            />
                        </div>

                        {/* MOBILE */}
                        <div className="lg:hidden flex items-center gap-2">
                            <BookACallButton
                                size="sm"
                                pulse
                                testId="book-a-call-mobile-top"
                            />
                            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                                <SheetTrigger asChild>
                                    <button
                                        aria-label="Open menu"
                                        data-testid="mobile-nav-open-button"
                                        className="h-11 w-11 inline-flex items-center justify-center rounded-xl border-2 border-[#0a0b0d]/15 hover:border-[#188bf6] transition-colors"
                                    >
                                        <Menu className="w-5 h-5 text-[#0a0b0d]" aria-hidden="true" />
                                    </button>
                                </SheetTrigger>
                                <SheetContent
                                    side="right"
                                    className="w-[92%] max-w-sm bg-white border-l border-[#0a0b0d]/10 text-[#0a0b0d] p-0"
                                >
                                    <SheetHeader className="p-5 border-b border-[#0a0b0d]/10 flex flex-row items-center justify-between">
                                        <SheetTitle className="text-[#0a0b0d] text-left font-display">
                                            <div className="flex items-center gap-3">
                                                <img
                                                    src="/assets/mascot_pose1.png"
                                                    alt=""
                                                    className="h-10 w-auto"
                                                    aria-hidden="true"
                                                />
                                                <span className="flex flex-col leading-none">
                                                    <span className="font-display font-extrabold text-base tracking-tight text-[#0a0b0d]">
                                                        ELEVARA
                                                    </span>
                                                    <span className="font-display font-bold text-[10px] tracking-[0.22em] text-[#188bf6] mt-0.5">
                                                        SYSTEMS
                                                    </span>
                                                </span>
                                            </div>
                                        </SheetTitle>
                                        <button
                                            aria-label="Close menu"
                                            data-testid="mobile-nav-close-button"
                                            className="h-9 w-9 inline-flex items-center justify-center rounded-lg border-2 border-[#0a0b0d]/15"
                                            onClick={() => setMobileOpen(false)}
                                        >
                                            <X className="w-4 h-4" aria-hidden="true" />
                                        </button>
                                    </SheetHeader>
                                    <div className="p-5 space-y-1">
                                        <div className="mb-4">
                                            <BookACallButton
                                                size="md"
                                                className="w-full"
                                                testId="book-a-call-mobile-nav-button"
                                            />
                                        </div>
                                        <NavLink
                                            to="/"
                                            end
                                            data-testid="mobile-nav-link-home"
                                            className={({ isActive }) =>
                                                cn(
                                                    "flex items-center justify-between px-3 py-3 rounded-lg text-base font-display font-bold",
                                                    isActive
                                                        ? "text-[#188bf6] bg-[#188bf6]/8"
                                                        : "text-[#0a0b0d] hover:bg-[#0a0b0d]/5",
                                                )
                                            }
                                        >
                                            Home
                                        </NavLink>

                                        <Accordion type="single" collapsible className="w-full">
                                            <AccordionItem value="systems" className="border-none">
                                                <AccordionTrigger
                                                    data-testid="mobile-nav-systems-trigger"
                                                    className="px-3 py-3 rounded-lg text-base font-display font-bold text-[#0a0b0d] hover:no-underline hover:bg-[#0a0b0d]/5"
                                                >
                                                    Systems
                                                </AccordionTrigger>
                                                <AccordionContent className="pt-1 pb-2">
                                                    <div className="pl-3 space-y-1">
                                                        {SYSTEMS.map((sys) => (
                                                            <Link
                                                                key={sys.slug}
                                                                to={`/systems/${sys.slug}`}
                                                                data-testid={`mobile-nav-systems-item-${sys.slug}`}
                                                                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-display font-semibold text-[#0a0b0d]/85 hover:text-[#188bf6] hover:bg-[#188bf6]/8"
                                                            >
                                                                <SystemIcon
                                                                    slug={sys.slug}
                                                                    className="w-4 h-4 text-[#188bf6]"
                                                                />
                                                                <span>{sys.name}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </AccordionContent>
                                            </AccordionItem>
                                        </Accordion>

                                        <NavLink
                                            to="/pricing"
                                            data-testid="mobile-nav-link-pricing"
                                            className={({ isActive }) =>
                                                cn(
                                                    "flex items-center justify-between px-3 py-3 rounded-lg text-base font-display font-bold",
                                                    isActive
                                                        ? "text-[#188bf6] bg-[#188bf6]/8"
                                                        : "text-[#0a0b0d] hover:bg-[#0a0b0d]/5",
                                                )
                                            }
                                        >
                                            Pricing
                                        </NavLink>

                                        <Accordion type="single" collapsible className="w-full">
                                            <AccordionItem value="about" className="border-none">
                                                <AccordionTrigger
                                                    data-testid="mobile-nav-about-trigger"
                                                    className="px-3 py-3 rounded-lg text-base font-display font-bold text-[#0a0b0d] hover:no-underline hover:bg-[#0a0b0d]/5"
                                                >
                                                    About
                                                </AccordionTrigger>
                                                <AccordionContent className="pt-1 pb-2">
                                                    <div className="pl-3 space-y-1">
                                                        {ABOUT_DROPDOWN.map((item) => (
                                                            <Link
                                                                key={item.to}
                                                                to={item.to}
                                                                data-testid={`mobile-nav-about-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                                                className="block px-3 py-2.5 rounded-lg text-sm font-display font-semibold text-[#0a0b0d]/85 hover:text-[#188bf6] hover:bg-[#188bf6]/8"
                                                            >
                                                                {item.label}
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </AccordionContent>
                                            </AccordionItem>
                                        </Accordion>

                                        <a
                                            href={BOOK_A_CALL_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="mobile-nav-contact-link"
                                            className="flex items-center justify-between px-3 py-3 rounded-lg text-base font-display font-bold text-[#0a0b0d] hover:text-[#188bf6] hover:bg-[#188bf6]/8"
                                        >
                                            <span>Contact Us</span>
                                            <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                                        </a>

                                        <a
                                            href={LOGIN_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="mobile-nav-login-link"
                                            className="flex items-center justify-between px-3 py-3 mt-2 rounded-lg text-base font-display font-bold border-2 border-[#0a0b0d]/12 text-[#0a0b0d] hover:border-[#188bf6]"
                                        >
                                            Login
                                            <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                                        </a>
                                    </div>
                                </SheetContent>
                            </Sheet>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
}

export default Navbar;
